/**
 * useSectionListLayout — układ LISTY kartoteki ustawiony przez administratora (kolumny, kolejność,
 * szerokości, reguły formatowania), pobierany do sekcji kokpitu decyzji TYLKO do odczytu.
 *
 * Dlaczego nie `useTableLayout`: sekcja nie potrzebuje listy wszystkich układów, tylko układu
 * aktywnego — czyta go z `GET /system-modules/:slug/layouts/active?layoutType=list`. Obie trasy układów
 * są bez bramki uprawnień (układ to konfiguracja tenanta, nie dane rekordu). UWAGA: BEZ
 * `layoutType=list` serwer zwraca układ FORMULARZA, a nie listy.
 *
 * Zwraca gotowy `TableLayoutConfig` (puste pola uzupełnione `emptyListConfig`), który sekcja podaje
 * do `ConfigurableTable` jako `previewLayoutConfig` (z `persistConfig={false}` — nic się nie zapisuje).
 * Brak układu / `listConfig` null lub `{}` = pusta lista kolumn = domyślna widoczność kolumn.
 *
 * Klucz zapytania celowo ROZŁĄCZNY z `['system-module-layout-active', slug]` (układ formularza) i
 * z `['table-layouts', …]` (układy zarządzane) — inny kształt danych pod tym samym kluczem
 * zepsułby cache pozostałych ekranów.
 */

import { emptyListConfig } from '@saas/shared';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { TableLayoutConfig } from '@/components/configurable-table';
import { api } from '@/lib/api';

interface ActiveListLayoutResponse {
  layout: { listConfig: Partial<TableLayoutConfig> | null } | null;
}

export function useSectionListLayout(moduleSlug: string): UseQueryResult<TableLayoutConfig> {
  return useQuery({
    queryKey: ['system-module-list-layout-active', moduleSlug],
    queryFn: async (): Promise<TableLayoutConfig> => {
      const res = await api.get<ActiveListLayoutResponse>(
        `/system-modules/${moduleSlug}/layouts/active?layoutType=list`,
      );
      // Pola brakujące w `listConfig` (null, `{}`, układ sprzed dodania pola) → wartości puste.
      return { ...emptyListConfig, ...(res.data.layout?.listConfig ?? {}) };
    },
    staleTime: 60_000,
  });
}
